// Google Analytics 和其他分析工具配置

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Google Analytics 4 配置 - Warp Zone Gems (aigame.lol)
const GA_MEASUREMENT_ID = 'G-WZG2024GAME'; // 实际部署时需要替换为真实的GA4测量ID
const GA_ENHANCED_ECOMMERCE = true; // 启用增强电商跟踪
const GA_DEBUG_MODE = process.env.NODE_ENV === 'development';

// SEO监控专用配置
export const SEO_TRACKING_CONFIG = {
  // 关键词排名监控
  targetKeywords: [
    '游戏下载', '3A游戏', '赛车游戏', '动作游戏推荐', '免费游戏',
    '单机游戏', '网页游戏', '休闲游戏', '策略游戏', '角色扮演游戏',
    'aigame.lol', 'Warp Zone Gems', '游戏网站', '在线游戏平台'
  ],
  // 竞争对手域名
  competitors: [
    'gamersky.com', 'gouhuo.qq.com', 'taptap.cn', 
    'steam233.com', '3dmgame.com', 'ali213.net'
  ],
  // 监控频率（分钟）
  monitoringInterval: 60,
  // 数据存储周期（天）
  dataRetentionDays: 90
};

// 初始化 Google Analytics
export const initializeGoogleAnalytics = () => {
  // 生产环境启用，开发环境可选启用用于测试
  if (process.env.NODE_ENV !== 'production' && !GA_DEBUG_MODE) {
    console.log('Google Analytics disabled in development');
    return;
  }

  // 检查是否已经加载
  if (window.gtag) {
    console.log('Google Analytics already loaded');
    return;
  }

  // 创建 dataLayer
  window.dataLayer = window.dataLayer || [];
  
  // 定义 gtag 函数
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };

  // 初始化
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    // 用户隐私设置
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    // 游戏网站专用配置
    custom_map: {
      'dimension1': 'game_category',
      'dimension2': 'game_platform', 
      'dimension3': 'user_segment',
      'dimension4': 'content_type'
    },
    // 增强电商配置
    send_page_view: true,
    debug_mode: GA_DEBUG_MODE
  });

  // 动态加载 GA 脚本
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  console.log('Google Analytics initialized');
};

// 页面浏览事件
export const trackPageView = (url: string, title?: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
      page_title: title || document.title,
    });
  }
};

// 自定义事件跟踪
export const trackEvent = (
  action: string,
  category: string = 'engagement',
  label?: string,
  value?: number,
  customParameters?: Record<string, any>
) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
      ...customParameters
    });
  }
};

// 游戏相关事件跟踪
export const trackGameEvent = (action: 'view' | 'download' | 'favorite' | 'share', gameId: string, gameTitle: string) => {
  trackEvent(action, 'game', `${gameTitle} (${gameId})`, undefined, {
    game_id: gameId,
    game_title: gameTitle
  });
};

// 搜索事件跟踪
export const trackSearch = (searchTerm: string, resultCount: number) => {
  trackEvent('search', 'site_search', searchTerm, resultCount, {
    search_term: searchTerm,
    result_count: resultCount
  });
};

// 下载事件跟踪
export const trackDownload = (fileName: string, fileUrl: string, gameId?: string) => {
  trackEvent('download', 'file_download', fileName, undefined, {
    file_name: fileName,
    file_url: fileUrl,
    game_id: gameId
  });
};

// 错误事件跟踪
export const trackError = (errorMessage: string, errorType: string = 'javascript', fatal: boolean = false) => {
  trackEvent('exception', 'error', errorMessage, undefined, {
    description: errorMessage,
    fatal: fatal,
    error_type: errorType
  });
};

// 性能事件跟踪
export const trackPerformance = (metricName: string, value: number, rating: string) => {
  trackEvent(metricName, 'web_vitals', rating, Math.round(value), {
    metric_value: value,
    metric_rating: rating
  });
};

// 转化事件跟踪
export const trackConversion = (action: string, value?: number, currency: string = 'USD') => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'conversion', {
      send_to: GA_MEASUREMENT_ID,
      event_category: 'conversion',
      event_label: action,
      value: value,
      currency: currency
    });
  }
};

// 用户engagement跟踪
export const trackEngagement = (action: string, duration?: number) => {
  trackEvent(action, 'user_engagement', undefined, duration, {
    engagement_time_msec: duration
  });
};

// 社交分享跟踪
export const trackSocialShare = (platform: string, url: string, title: string) => {
  trackEvent('share', 'social', platform, undefined, {
    method: platform,
    content_type: 'game',
    item_id: url,
    content_title: title
  });
};

// 创建自动跟踪路由变化的Hook
export const useAnalyticsPageTracking = () => {
  const trackPageChange = (location: Location) => {
    const path = location.pathname + location.search + location.hash;
    trackPageView(path);
  };

  return { trackPageChange };
};

// 百度统计配置（中文游戏网站专用）
const BAIDU_ANALYTICS_ID = 'e6c4a8b9d1f2a5c7b8e9f0a1'; // Warp Zone Gems 百度统计ID
const BAIDU_ENHANCED_TRACKING = true; // 启用增强跟踪

export const initializeBaiduAnalytics = () => {
  if (process.env.NODE_ENV !== 'production' && !BAIDU_ENHANCED_TRACKING) {
    console.log('Baidu Analytics disabled in development');
    return;
  }

  // 百度统计代码 - 游戏网站增强版
  const script = document.createElement('script');
  script.innerHTML = `
    var _hmt = _hmt || [];
    (function() {
      var hm = document.createElement("script");
      hm.src = "https://hm.baidu.com/hm.js?${BAIDU_ANALYTICS_ID}";
      var s = document.getElementsByTagName("script")[0]; 
      s.parentNode.insertBefore(hm, s);
      
      // 游戏网站专用配置
      _hmt.push(['_setAutoPageview', false]);
      _hmt.push(['_setCustomVar', 1, 'site_type', 'gaming', 1]);
      _hmt.push(['_setCustomVar', 2, 'content_language', 'zh-CN', 2]);
    })();
  `;
  document.head.appendChild(script);

  // 设置全局变量
  (window as any)._hmt = (window as any)._hmt || [];

  console.log('Baidu Analytics initialized');
};

// 百度统计事件跟踪
export const trackBaiduEvent = (category: string, action: string, label?: string, value?: number) => {
  if (typeof (window as any)._hmt !== 'undefined') {
    (window as any)._hmt.push(['_trackEvent', category, action, label, value]);
  }
};

// 百度统计页面跟踪
export const trackBaiduPageView = (url: string) => {
  if (typeof (window as any)._hmt !== 'undefined') {
    (window as any)._hmt.push(['_trackPageview', url]);
  }
};

// 初始化所有分析工具
export const initializeAnalytics = () => {
  // Google Analytics
  initializeGoogleAnalytics();
  
  // 百度统计（针对中文用户）
  initializeBaiduAnalytics();
  
  // 设置全局错误处理
  window.addEventListener('error', (event) => {
    trackError(event.message, 'javascript', true);
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackError(event.reason?.toString() || 'Unhandled Promise Rejection', 'promise', true);
  });
};

// 隐私友好的分析配置
export const configurePrivacyFriendlyAnalytics = () => {
  // 检查用户是否同意cookies
  const hasConsent = localStorage.getItem('analytics_consent') === 'true';
  
  if (!hasConsent) {
    // 显示cookie同意横幅或使用无cookie的分析
    console.log('Analytics consent not granted');
    return false;
  }

  initializeAnalytics();
  return true;
};

// 设置分析同意
export const setAnalyticsConsent = (consent: boolean) => {
  localStorage.setItem('analytics_consent', consent.toString());
  
  if (consent) {
    initializeAnalytics();
  } else {
    // 清除现有的分析cookies和数据
    document.cookie.split(";").forEach((c) => {
      document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });
  }
};

// =========================
// SEO专用跟踪功能
// =========================

// SEO关键事件跟踪
export const trackSEOEvent = (action: string, data: Record<string, any> = {}) => {
  trackEvent(action, 'seo', data.label || action, data.value, {
    ...data,
    timestamp: new Date().toISOString(),
    page_url: window.location.href,
    referrer: document.referrer
  });
};

// 关键词排名跟踪
export const trackKeywordRanking = (keyword: string, position: number, searchEngine: string = 'google') => {
  trackSEOEvent('keyword_ranking', {
    label: keyword,
    value: position,
    keyword,
    position,
    search_engine: searchEngine,
    domain: window.location.hostname
  });
};

// 页面SEO指标跟踪
export const trackPageSEO = (pageData: {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl: string;
  structuredData?: any;
}) => {
  trackSEOEvent('page_seo_audit', {
    label: pageData.title,
    page_title: pageData.title,
    meta_description: pageData.description,
    meta_keywords: pageData.keywords.join(','),
    canonical_url: pageData.canonicalUrl,
    has_structured_data: !!pageData.structuredData,
    title_length: pageData.title.length,
    description_length: pageData.description.length,
    keywords_count: pageData.keywords.length
  });
};

// 搜索引擎爬虫访问跟踪
export const trackCrawlerVisit = (userAgent: string, botName: string) => {
  trackSEOEvent('crawler_visit', {
    label: botName,
    user_agent: userAgent,
    bot_name: botName,
    crawl_timestamp: new Date().toISOString()
  });
};

// 站点地图使用跟踪
export const trackSitemapUsage = (sitemapType: 'xml' | 'html', requestCount: number) => {
  trackSEOEvent('sitemap_access', {
    label: `${sitemapType}_sitemap`,
    value: requestCount,
    sitemap_type: sitemapType,
    access_count: requestCount
  });
};

// SERP点击跟踪（来自搜索引擎的流量）
export const trackSERPClick = (query: string, position: number, searchEngine: string) => {
  trackSEOEvent('serp_click', {
    label: query,
    value: position,
    search_query: query,
    serp_position: position,
    search_engine: searchEngine,
    landing_page: window.location.pathname
  });
};

// 内部链接点击跟踪
export const trackInternalLinkClick = (fromPage: string, toPage: string, linkText: string) => {
  trackSEOEvent('internal_link_click', {
    label: `${fromPage} -> ${toPage}`,
    from_page: fromPage,
    to_page: toPage,
    link_text: linkText,
    click_timestamp: new Date().toISOString()
  });
};

// 404错误跟踪
export const track404Error = (requestedUrl: string, referrer?: string) => {
  trackSEOEvent('404_error', {
    label: requestedUrl,
    requested_url: requestedUrl,
    referrer: referrer || document.referrer,
    error_type: '404_not_found'
  });
};

// 结构化数据验证跟踪
export const trackStructuredDataValidation = (type: string, isValid: boolean, errors?: string[]) => {
  trackSEOEvent('structured_data_validation', {
    label: type,
    structured_data_type: type,
    is_valid: isValid,
    validation_errors: errors ? errors.join(';') : '',
    error_count: errors ? errors.length : 0
  });
};

// 页面加载速度与SEO关联跟踪
export const trackPageSpeedSEO = (metrics: {
  loadTime: number;
  domContentLoaded: number;
  firstContentfulPaint: number;
  largestContentfulPaint: number;
}) => {
  trackSEOEvent('page_speed_seo', {
    label: window.location.pathname,
    value: Math.round(metrics.loadTime),
    load_time: metrics.loadTime,
    dom_content_loaded: metrics.domContentLoaded,
    first_contentful_paint: metrics.firstContentfulPaint,
    largest_contentful_paint: metrics.largestContentfulPaint,
    page_path: window.location.pathname
  });
};

// 移动端SEO友好性跟踪
export const trackMobileSEO = (metrics: {
  viewportConfigured: boolean;
  touchElementsSpaced: boolean;
  textReadable: boolean;
  contentSizedToViewport: boolean;
}) => {
  trackSEOEvent('mobile_seo_audit', {
    label: 'mobile_friendliness',
    viewport_configured: metrics.viewportConfigured,
    touch_elements_spaced: metrics.touchElementsSpaced,
    text_readable: metrics.textReadable,
    content_sized_to_viewport: metrics.contentSizedToViewport,
    mobile_score: Object.values(metrics).filter(Boolean).length / Object.values(metrics).length * 100
  });
};

// 自动检测和跟踪SEO相关事件
export const setupAutoSEOTracking = () => {
  // 检测是否来自搜索引擎
  const referrer = document.referrer;
  const searchEngines = ['google.', 'bing.', 'baidu.', 'yahoo.', 'duckduckgo.'];
  
  if (referrer && searchEngines.some(engine => referrer.includes(engine))) {
    const url = new URL(referrer);
    const query = url.searchParams.get('q') || url.searchParams.get('query') || '';
    const searchEngine = searchEngines.find(engine => referrer.includes(engine))?.replace('.', '') || 'unknown';
    
    if (query) {
      trackSERPClick(query, 1, searchEngine); // 位置信息需要通过Search Console API获取
    }
  }

  // 自动跟踪内部链接点击
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    const link = target.closest('a');
    
    if (link && link.hostname === window.location.hostname) {
      const fromPage = window.location.pathname;
      const toPage = new URL(link.href).pathname;
      const linkText = link.textContent?.trim() || link.getAttribute('aria-label') || 'No text';
      
      trackInternalLinkClick(fromPage, toPage, linkText);
    }
  });

  // 自动检测爬虫访问
  const userAgent = navigator.userAgent.toLowerCase();
  const bots = [
    { name: 'Googlebot', pattern: /googlebot/i },
    { name: 'Bingbot', pattern: /bingbot/i },
    { name: 'Baiduspider', pattern: /baiduspider/i },
    { name: 'YandexBot', pattern: /yandexbot/i },
    { name: 'facebookexternalhit', pattern: /facebookexternalhit/i }
  ];

  const detectedBot = bots.find(bot => bot.pattern.test(userAgent));
  if (detectedBot) {
    trackCrawlerVisit(navigator.userAgent, detectedBot.name);
  }
};